/**
 * Rotates a 32-bit integer right by a number of bits (SHA-256 helper).
 *
 * @param value - The 32-bit value.
 * @param amount - Bits to rotate by.
 * @returns The rotated value.
 */
function rotateRight(value: number, amount: number) {
   return (value >>> amount) | (value << (32 - amount))
}

/**
 * Computes a SHA-256 hex digest in pure JS, used when the Web Crypto API is unavailable.
 *
 * @param value - The string to hash.
 * @returns The 64-character hex digest.
 */
function sha256HexFallback(value: string) {
   const roundConstants = [
      0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
      0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
      0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f,
      0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
      0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
      0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
      0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
      0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
      0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
      0xc67178f2,
   ]
   const hash = [
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab,
      0x5be0cd19,
   ]
   const schedule = new Array<number>(64).fill(0)
   const words: number[] = []
   const utf8 = new TextEncoder().encode(value)

   for (let index = 0; index < utf8.length; index += 1) {
      words[index >> 2] =
         (words[index >> 2] || 0) | ((utf8[index] ?? 0) << (24 - (index % 4) * 8))
   }

   words[utf8.length >> 2] =
      (words[utf8.length >> 2] || 0) | (0x80 << (24 - (utf8.length % 4) * 8))
   words[(((utf8.length + 8) >> 6) << 4) + 15] = utf8.length * 8

   for (let chunkIndex = 0; chunkIndex < words.length; chunkIndex += 16) {
      for (let index = 0; index < 16; index += 1) {
         schedule[index] = words[chunkIndex + index] || 0
      }

      for (let index = 16; index < 64; index += 1) {
         const sigma0 =
            rotateRight(schedule[index - 15] ?? 0, 7) ^
            rotateRight(schedule[index - 15] ?? 0, 18) ^
            ((schedule[index - 15] ?? 0) >>> 3)
         const sigma1 =
            rotateRight(schedule[index - 2] ?? 0, 17) ^
            rotateRight(schedule[index - 2] ?? 0, 19) ^
            ((schedule[index - 2] ?? 0) >>> 10)
         schedule[index] =
            ((((schedule[index - 16] ?? 0) + sigma0) | 0) +
               (((schedule[index - 7] ?? 0) + sigma1) | 0)) |
            0
      }

      let a = hash[0] ?? 0
      let b = hash[1] ?? 0
      let c = hash[2] ?? 0
      let d = hash[3] ?? 0
      let e = hash[4] ?? 0
      let f = hash[5] ?? 0
      let g = hash[6] ?? 0
      let h = hash[7] ?? 0

      for (let index = 0; index < 64; index += 1) {
         const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)
         const choice = (e & f) ^ (~e & g)
         const temp1 =
            (((h + sum1) | 0) +
               ((choice + (roundConstants[index] ?? 0)) | 0) +
               (schedule[index] ?? 0)) |
            0
         const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)
         const majority = (a & b) ^ (a & c) ^ (b & c)
         const temp2 = (sum0 + majority) | 0

         h = g
         g = f
         f = e
         e = (d + temp1) | 0
         d = c
         c = b
         b = a
         a = (temp1 + temp2) | 0
      }

      hash[0] = ((hash[0] ?? 0) + a) | 0
      hash[1] = ((hash[1] ?? 0) + b) | 0
      hash[2] = ((hash[2] ?? 0) + c) | 0
      hash[3] = ((hash[3] ?? 0) + d) | 0
      hash[4] = ((hash[4] ?? 0) + e) | 0
      hash[5] = ((hash[5] ?? 0) + f) | 0
      hash[6] = ((hash[6] ?? 0) + g) | 0
      hash[7] = ((hash[7] ?? 0) + h) | 0
   }

   return hash.map(part => (part >>> 0).toString(16).padStart(8, '0')).join('')
}

/**
 * Strips trailing slashes from a sync base URL.
 *
 * @param base - The base URL.
 * @returns The sanitized base URL.
 */
export function sanitizeSyncBase(base: string) {
   if (!base) {
      return ''
   }

   let sanitized = base
   while (sanitized.endsWith('/')) {
      sanitized = sanitized.slice(0, -1)
   }

   return sanitized
}

/**
 * Hashes a sync key into a user id via Web Crypto, falling back to a JS SHA-256 implementation.
 *
 * @param userId - The raw sync key.
 * @param hashAlgorithm - The Web Crypto digest algorithm (e.g. `SHA-256`).
 * @returns The hashed user id, or an empty string when no key is given.
 */
export async function buildHashedUserId(userId: string, hashAlgorithm: string) {
   if (!userId) {
      return ''
   }

   try {
      if (crypto && crypto.subtle && typeof crypto.subtle.digest === 'function') {
         const encoder = new TextEncoder()
         const data = encoder.encode(userId)
         const hashBuffer = await crypto.subtle.digest(hashAlgorithm, data)
         return Array.from(new Uint8Array(hashBuffer))
            .map(byte => byte.toString(16).padStart(2, '0'))
            .join('')
      }

      return sha256HexFallback(userId)
   } catch (error) {
      console.error('Failed to hash user ID', error)
      try {
         return sha256HexFallback(userId)
      } catch (fallbackError) {
         console.error('Fallback SHA-256 hashing failed', fallbackError)
         return userId
      }
   }
}
